import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MagnifyingGlass, Phone, Envelope, UserCircle, Users, Trophy, Medal } from "@phosphor-icons/react";
import UserAvatar from "@/components/UserAvatar";
import OrganizerBadge from "@/components/OrganizerBadge";


const roleColors = {
  admin: "bg-[#CCFF00]/20 text-[#CCFF00] border-[#CCFF00]/30",
  directivo: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  profesor: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  jugador: "bg-gray-500/20 text-gray-300 border-gray-500/30",
};

const ROLE_FILTERS = [
  { key: "all", label: "Todos" },
  { key: "jugador", label: "Jugadores" },
  { key: "profesor", label: "Profesores" },
  { key: "directivo", label: "Directivos" },
  { key: "admin", label: "Admin" },
];

export default function PlayersDirectory() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [ranking, setRanking] = useState({});
  const [topBadges, setTopBadges] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sportFilter, setSportFilter] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/users"), api.get("/statistics/ranking")])
      .then(([u, r]) => {
        setUsers(u.data);
        const map = {};
        (r.data || []).forEach((s, idx) => { map[s.user_id] = { ...s, position: idx + 1 }; });
        setRanking(map);
        // Batch fetch top 3 badges for all loaded users
        const ids = (u.data || []).map((p) => p.id).filter(Boolean).join(",");
        if (ids) {
          api.get(`/users/badges-summary?ids=${ids}&per_user=3`)
            .then(({ data }) => setTopBadges(data || {}))
            .catch(() => {});
        }
      })
      .catch(() => toast.error("Error al cargar usuarios"))
      .finally(() => setLoading(false));
  }, []);

  const enriched = users.map((p) => ({ ...p, stats: ranking[p.id] || null }));
  const filtered = enriched
    .filter((p) => (roleFilter === "all" ? true : p.role === roleFilter))
    .filter((p) => {
      if (sportFilter === "all") return true;
      const ps = p.sport || "padel";
      if (sportFilter === "ambos") return ps === "ambos";
      // sportFilter "padel" matches padel or ambos; "tenis" matches tenis or ambos
      return ps === sportFilter || ps === "ambos";
    })
    .filter((p) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.email || "").toLowerCase().includes(q) ||
        (p.phone || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const ap = a.stats?.position ?? 9999;
      const bp = b.stats?.position ?? 9999;
      if (ap !== bp) return ap - bp;
      return (a.name || "").localeCompare(b.name || "");
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="players-directory-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Comunidad</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Jugadores y Profesores</h1>
          <p className="text-xs text-gray-500 mt-1 font-['JetBrains_Mono']">
            {users.filter((u) => u.role === "jugador").length} jugadores · {users.filter((u) => u.role === "profesor").length} profesores · {users.filter((u) => u.role === "directivo").length} directivos
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            data-testid="players-search"
            placeholder="Buscar por nombre, email o telefono..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-[#0A0A0A] border-white/20 text-white h-11 pl-10 focus:border-[#CCFF00]"
          />
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="role-filter">
          {ROLE_FILTERS.map((r) => (
            <button
              key={r.key}
              onClick={() => setRoleFilter(r.key)}
              className={`px-3 h-11 text-xs font-semibold uppercase tracking-wider border transition-colors ${
                roleFilter === r.key
                  ? "bg-[#CCFF00] text-black border-[#CCFF00]"
                  : "bg-[#0A0A0A] text-gray-400 border-white/20 hover:border-[#CCFF00]/30 hover:text-[#CCFF00]"
              }`}
              data-testid={`filter-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {/* Sport filter */}
        <div className="flex gap-2 flex-wrap items-center" data-testid="sport-filter">
          <span className="text-[10px] uppercase tracking-widest text-gray-500 mr-1">Deporte:</span>
          {[
            { key: "all", label: "Todos" },
            { key: "padel", label: "🎾 Pádel" },
            { key: "tenis", label: "🎾 Tenis" },
            { key: "ambos", label: "🎾 Ambos" },
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => setSportFilter(s.key)}
              className={`px-3 h-9 text-[11px] font-semibold uppercase tracking-wider border transition-colors ${
                sportFilter === s.key
                  ? "bg-[#CCFF00] text-black border-[#CCFF00]"
                  : "bg-[#0A0A0A] text-gray-400 border-white/20 hover:border-[#CCFF00]/30 hover:text-[#CCFF00]"
              }`}
              data-testid={`sport-filter-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card-tactical p-12 text-center">
          <Users size={48} weight="duotone" className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">{users.length === 0 ? "Aun no hay usuarios registrados" : "Sin resultados"}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const pos = p.stats?.position;
            const podium = pos === 1 ? "text-yellow-400" : pos === 2 ? "text-gray-300" : pos === 3 ? "text-orange-400" : null;
            const isPlayer = p.role === "jugador";
            const isAdmin = user?.role === "admin";
            return (
              <Link
                key={p.id}
                to={isPlayer ? `/dashboard/jugador/${p.id}` : "#"}
                onClick={(e) => { if (!isPlayer) e.preventDefault(); }}
                className={`card-tactical p-5 flex items-center gap-4 transition-colors ${isPlayer ? "hover:border-[#CCFF00]/40" : "cursor-default"}`}
                data-testid={`player-card-${p.id}`}
              >
                {p.profile_photo_url ? (
                  <img src={p.profile_photo_url} alt={p.name} className="w-14 h-14 rounded-full object-cover border border-white/10 shrink-0" />
                ) : (
                  <UserAvatar user={p} size={56} className="border border-white/10 shrink-0" testId={`player-avatar-${p.id}`} topBadges={topBadges[p.id] || []} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-white truncate">{p.name}</h3>
                    <OrganizerBadge user={p} />
                    {isAdmin && (
                      <>
                        <Badge variant="outline" className={`badge-status ${roleColors[p.role] || roleColors.jugador} text-[10px]`}>{p.role}</Badge>
                        {pos && (
                          <Badge variant="outline" className={`badge-status text-[10px] ${podium ? `${podium} border-current/40 bg-current/10` : "bg-white/10 text-gray-300 border-white/20"}`}>
                            {podium ? <Medal size={10} weight="fill" className="mr-0.5" /> : null}#{pos}
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                  {/* Top 3 badges showcase */}
                  {(topBadges[p.id] || []).length > 0 && (
                    <div className="flex items-center gap-1 mt-1.5" data-testid={`player-badges-${p.id}`}>
                      {(topBadges[p.id] || []).map((b) => (
                        <span
                          key={b.id}
                          className="text-base leading-none"
                          title={`${b.name} · ${(b.earned_at || "").slice(0, 10)}`}
                          data-testid={`player-badge-${p.id}-${b.code}`}
                        >
                          {b.icon}
                        </span>
                      ))}
                    </div>
                  )}
                  {isAdmin && p.stats && (
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 font-['JetBrains_Mono']">
                      <span className="text-[#CCFF00]"><Trophy size={10} weight="fill" className="inline mr-0.5" />{p.stats.points || 0} pts</span>
                      <span>{p.stats.games_won || 0}W / {Math.max(0, (p.stats.games_played || 0) - (p.stats.games_won || 0))}L</span>
                    </div>
                  )}
                  {(p.category || p.experience_years != null || p.state || p.sport) && (
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-1 flex-wrap">
                      {(() => {
                        const sp = p.sport || "padel";
                        return (
                          <Badge variant="outline" className="text-[10px] px-2 py-0"
                            style={{
                              color: sp === "tenis" ? "#FFB800" : sp === "ambos" ? "#00C853" : "#CCFF00",
                              borderColor: sp === "tenis" ? "rgba(255,184,0,0.4)" : sp === "ambos" ? "rgba(0,200,83,0.4)" : "rgba(204,255,0,0.4)",
                              backgroundColor: sp === "tenis" ? "rgba(255,184,0,0.08)" : sp === "ambos" ? "rgba(0,200,83,0.08)" : "rgba(204,255,0,0.08)",
                            }}
                            data-testid={`player-sport-${p.id}`}
                          >
                            {sp === "tenis" ? "🎾 Tenis" : sp === "ambos" ? "🎾 Ambos" : "🎾 Pádel"}
                          </Badge>
                        );
                      })()}
                      {p.category && (
                        <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[10px] px-2 py-0" data-testid={`player-cat-${p.id}`}>
                          Cat. {["A","B","C","D"].includes(p.category) ? p.category : p.category === "open" ? "Open" : p.category === "1" ? "1ra" : p.category === "2" ? "2da" : p.category === "3" ? "3ra" : p.category === "4" ? "4ta" : p.category === "5" ? "5ta" : "6ta"}
                        </Badge>
                      )}
                      {p.state && (
                        <Badge variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px] px-2 py-0" data-testid={`player-state-${p.id}`}>
                          {p.state}{p.country && p.country !== "MX" ? `, ${p.country}` : ""}
                        </Badge>
                      )}
                      {isAdmin && p.experience_years != null && (
                        <span className="font-['JetBrains_Mono']">
                          {p.experience_years < 1 ? `${Math.round(p.experience_years * 12)} meses` : `${p.experience_years} anos`} {p.role === "profesor" ? "ensenando" : "jugando"}
                        </span>
                      )}
                    </div>
                  )}
                  {isAdmin && p.phone && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                      <Phone size={12} /> {p.phone}
                    </div>
                  )}
                  {isAdmin && p.email && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5 truncate">
                      <Envelope size={12} /> <span className="truncate">{p.email}</span>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
